import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MealFeedCard } from '@/components/public/MealFeedCard';
import SEOHead from '@/components/SEOHead';
import { supabase } from '@/integrations/supabase/client';
import { parseISO, format } from 'date-fns';

interface MealFeedItem {
  id: string;
  date: string;
  mealType: string;
  recipeTitle: string;
  recipeId: string;
  tags: string[];
  averageRating: number | null;
}

// Fisher-Yates shuffle for serendipity
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const fetchMealFeed = async (): Promise<MealFeedItem[]> => {
  const { data, error } = await supabase.functions.invoke('public-meal-feed');
  
  if (error) {
    console.error('Error fetching meal feed:', error);
    throw error;
  }
  
  // Shuffle for serendipity - not chronological
  return shuffleArray(data?.meals || []);
};

const MealInspiration: React.FC = () => {
  const { data: meals, isLoading, error } = useQuery({
    queryKey: ['public-meal-feed'],
    queryFn: fetchMealFeed,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Meal Inspiration | Real Family Meal Plans",
    "description": "See what real families are cooking this week. Get inspired by actual meal plans and discover new recipe ideas.",
    "url": "https://forkast.app/meal-inspiration",
  };

  return (
    <>
      <SEOHead
        title="Meal Inspiration | Real Family Meal Plans - forkast"
        description="See what real families are cooking this week. Get inspired by actual meal plans and discover new recipe ideas for breakfast, lunch, and dinner."
        canonical="/meal-inspiration"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="py-6 md:py-10 border-b border-border/30">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Live meal feed</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
              What families are cooking
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Real meal plans from real families. Get inspired by what others are cooking 
              for breakfast, lunch, and dinner this week.
            </p>
          </div>
        </section>

        {/* Meal Feed Grid */}
        <section className="py-8 md:py-10">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-muted-foreground">Unable to load meal feed. Please try again later.</p>
              </div>
            ) : meals && meals.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {meals.map((meal) => {
                  const dayOfWeek = meal.date ? format(parseISO(meal.date), 'EEEE') : undefined;
                  return (
                    <MealFeedCard
                      key={meal.id}
                      mealType={meal.mealType}
                      recipeTitle={meal.recipeTitle}
                      tags={meal.tags}
                      dayOfWeek={dayOfWeek}
                      averageRating={meal.averageRating}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-muted-foreground">No meals to show yet. Check back soon!</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-background to-muted/30 border-t border-border/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Ready to plan your meals?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              Join families who've simplified their weekly meal planning. 
              Save time, reduce food waste, and eat better together.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth/register">
                <Button size="lg" className="gap-2">
                  Start planning for free
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/how-forkast-meal-planning-works">
                <Button variant="outline" size="lg">
                  Learn how it works
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default MealInspiration;
